import { Button, FlexView, InformativaPrivacy, Spacer, Alert } from '@facile-it/shark-morph/components'
import * as O from 'fp-ts/Option'
import { pipe } from 'fp-ts/function'
import * as React from 'react'
import { Unit } from '../../../../../../../shark-lib/ts/services/Form/Unit'
import { formBuild } from '../../../../../../../shark-lib/ts/services/Form/formBuilder'
import {
    buildMultipleValidator,
    buildSingleValidator,
} from '../../../../../../../shark-lib/ts/services/Form/validators/Validator'
import { compareValidator } from '../../../../../../../shark-lib/ts/services/Form/validators/constraints/compareValidator'
import { emailValidator } from '../../../../../../../shark-lib/ts/services/Form/validators/constraints/emailValidator'
import { phoneAndEmailAtLeastOneFilledValidator } from '../../../../../../../shark-lib/ts/services/Form/validators/constraints/phoneAndEmailAtLeastOneFilledValidator'
import { regexValidator } from '../../../../../../../shark-lib/ts/services/Form/validators/constraints/regexValidator'
import { informativaPrivacyBuilder } from '../../../../../../../shark-lib/ts/services/InformativaPrivacy/informativaPrivacyBuilder'
import type { BadRequestResponse } from '../../../../../../../shark-lib/ts/types/BadRequestResponse'
import type { Request } from '../../../../../../../shark-lib/ts/types/Request'
import { translateToResource } from '../../../../../../../shark-lib/ts/types/Request'
import type { Prospect } from '../../../../models/prospect'
import { CitiesProvisioner } from '../../../../services/Form/provisioner/CitiesProvisioner'
import { COUPON_REGEX, FORM_VALIDATOR_REGEX } from '.'

export namespace FormProspect {
    export interface Props {
        coupon: O.Option<string>
        onSubmitProspect: (data: Prospect) => void
        postResource: Request<unknown, BadRequestResponse>
    }
}

export const FormProspect: React.FC<FormProspect.Props> = ({ coupon, onSubmitProspect, postResource }) => {
    function getValidCoupon(): O.Option<string> {
        return pipe(
            coupon,
            O.chainNullableK(c => c.match(COUPON_REGEX)),
            O.map(matched => matched[0])
        )
    }

    return (
        <>
            {formBuild({
                name: 'formProspect',
                formStyle: {
                    inline: 'all50',
                    borderless: true,
                },
                unitsForm: {
                    firstName: Unit.buildFormUnit({
                        componentType: 'input',
                        label: 'Nome*',
                        props: {
                            type: 'text',
                        },
                        notBlank: true,
                        tags: [],
                    }),
                    lastName: Unit.buildFormUnit({
                        componentType: 'input',
                        label: 'Cognome*',
                        props: {
                            type: 'text',
                        },
                        notBlank: true,
                        tags: [],
                    }),
                    phone: Unit.buildFormUnit({
                        componentType: 'input',
                        label: 'Cellulare*',
                        props: {
                            type: 'text',
                            debounceTime: 400,
                        },
                        validators: {
                            r: buildSingleValidator(regexValidator, { regex: new RegExp(/^\d+$/i) }),
                        },
                        notBlank: true,
                        tags: [],
                    }),
                    email: Unit.buildFormUnit({
                        componentType: 'input',
                        label: 'Email',
                        props: {
                            type: 'text',
                            debounceTime: 400,
                        },
                        validators: {
                            e: buildSingleValidator(emailValidator, {}),
                        },
                        notBlank: false,
                        tags: [],
                    }),
                    customerPortfolio: Unit.buildFormUnit({
                        componentType: 'selectClassic',
                        label: 'Valore portafoglio/€',
                        props: {
                            labelOptions: ' ',
                            options: [
                                { label: 'minore di 250.000 €', value: 'minore di 250.000 €' },
                                { label: 'tra 250.000 € e 500.000 €', value: 'tra 250.000 € e 500.000 €' },
                                { label: 'maggiore di 500.000 €', value: 'maggiore di 500.000 €' },
                            ],
                        },
                        notBlank: false,
                        tags: [],
                    }),
                    ruiCode: Unit.buildFormUnit({
                        componentType: 'input',
                        label: 'Iscrizione RUI*',
                        props: {
                            type: 'text',
                            debounceTime: 400,
                        },
                        validators: {
                            r: buildSingleValidator(regexValidator, { regex: /^[ABE]{1}\d{9}$/ }),
                        },
                        notBlank: true,
                        tags: [],
                    }),
                    headquartersProvince: Unit.buildFormUnit({
                        componentType: 'chosen',
                        label: 'Provincia sede operativa*',
                        props: {
                            provisioner: new CitiesProvisioner({}),
                        },
                        notBlank: true,
                        tags: [],
                    }),
                    numberOfBrokers: Unit.buildFormUnit({
                        componentType: 'selectClassic',
                        label: 'Numero intermediari per cui opera',
                        props: {
                            labelOptions: ' ',
                            options: [
                                { label: '1', value: '1' },
                                { label: '2', value: '2' },
                                { label: '3', value: '3' },
                                { label: 'maggiore di 3', value: 'maggiore di 3' },
                            ],
                        },
                        notBlank: false,
                        tags: [],
                    }),
                    coupon: Unit.buildFormUnit({
                        componentType: 'input',
                        label: 'Coupon',
                        initData: pipe(getValidCoupon(), O.toUndefined),
                        props: {
                            type: 'text',
                            debounceTime: 400,
                            disabled: pipe(getValidCoupon(), O.isSome),
                        },
                        validators: {
                            r: buildSingleValidator(regexValidator, { regex: FORM_VALIDATOR_REGEX }),
                        },
                        notBlank: false,
                        tags: [],
                    }),
                    informativaAccepted: Unit.buildFormUnit({
                        componentType: 'choiceOption',
                        label: '',
                        props: {
                            type: 'checkbox',
                            label: '',
                        },
                        validators: {
                            equals: buildSingleValidator(compareValidator, {
                                operation: 'equal',
                                to: 1,
                                invalidMessage: "Per continuare devi accettare l'informativa",
                            }),
                        },
                        notBlank: false,
                        tags: [],
                    }),
                },
                unitsHtml: () => ({
                    space: Unit.buildHtmlUnit({
                        element: <Spacer height={1} />,
                    }),
                }),
                structure: () => [
                    {
                        rows: [
                            {
                                units: [
                                    { name: 'firstName', view: { col: 12 } },
                                    { name: 'lastName', view: { col: 12 } },
                                    { name: 'phone', view: { col: 12 } },
                                    { name: 'email', view: { col: 12 } },
                                    { name: 'customerPortfolio', view: { col: 12 } },
                                    { name: 'headquartersProvince', view: { col: 12 } },
                                    { name: 'numberOfBrokers', view: { col: 12 } },
                                    { name: 'ruiCode', view: { col: 12 } },
                                    { name: 'coupon', view: { col: 12 }, hide: true },
                                ],
                            },
                            {
                                units: [
                                    { name: 'space', view: { col: 12 } },
                                    {
                                        name: 'informativaAccepted',
                                        view: {
                                            col: 12,
                                            flex: true,
                                            suffix: <InformativaPrivacy {...informativaPrivacyBuilder()} />,
                                        },
                                    },
                                ],
                            },
                        ],
                    },
                ],
                validators: {
                    phone: buildMultipleValidator(
                        phoneAndEmailAtLeastOneFilledValidator,
                        ['phone', 'email'],
                        'phone',
                        {}
                    ),
                },
                submit: {
                    type: 'custom',
                    submit: (
                        <FlexView hAlignContent="center" vAlignContent="center">
                            <FlexView.Item xs={12}>
                                <Spacer height={0.5} />
                            </FlexView.Item>
                            <FlexView.Item xs={12} lg={7} xl={6}>
                                <Button type="submit" styleType="success" size="md" stretch>
                                    Invia i dati »
                                </Button>
                            </FlexView.Item>
                        </FlexView>
                    ),
                },
                onSubmit: (_form, fields) => {
                    const payload = {
                        firstName: fields.firstName.value.toString(),
                        lastName: fields.lastName.value.toString(),
                        phone: fields.phone.value.toString(),
                        ruiCode: fields.ruiCode.value.toString(),
                        ...(fields.email.value ? { email: fields.email.value.toString() } : {}),
                        informativaAccepted: fields.informativaAccepted.value,
                        ...(fields.coupon.value !== undefined
                            ? { coupon: fields.coupon.value.toString().toUpperCase() }
                            : {}),
                        headquartersProvince: fields.headquartersProvince.value.toString(),
                        ...(fields.numberOfBrokers.value !== undefined
                            ? { numberOfBrokers: fields.numberOfBrokers.value.toString() }
                            : {}),
                        ...(fields.customerPortfolio.value !== undefined
                            ? { customerPortfolio: fields.customerPortfolio.value.toString() }
                            : {}),
                    }

                    onSubmitProspect(payload)
                },
                resource: {
                    state: {
                        resource: translateToResource(postResource),
                        isSubmitted: true,
                        canReject: true,
                        canFail: false,
                    },
                    onRejected: () => {
                        if (postResource.status === 'rejected') {
                            if (postResource.data) {
                                return (
                                    <Alert type="info">
                                        Dati non validi{' '}
                                        <ul>
                                            {postResource.data.errors.map((e, i) => (
                                                <li key={`e_${i}`}>{e}</li>
                                            ))}
                                        </ul>
                                    </Alert>
                                )
                            }

                            return <Alert type="info">Qualcosa è andato storto, riprovare più tardi</Alert>
                        }

                        return null
                    },
                },
            })}
        </>
    )
}
